import { z } from 'zod';
/**
 * **Contrats d'entrée de `CDC-3` — l'espace de fichiers et le partage de dossier.**
 *
 * Écrits une fois ici, ils valident l'entrée de l'API et valideraient les mêmes
 * formulaires côté front sans être réécrits (`DOCTRINE.md` §4). `strictObject`
 * partout : une clé inconnue produit un 400 visible, elle ne disparaît pas en
 * silence — asymétrie voulue avec la sortie, où un champ non déclaré est retiré
 * sans rien dire (D-021).
 *
 * **Chaque champ porte un `.describe()`** — seul `.describe()` atteint OpenAPI,
 * donc le client généré, donc l'autocomplétion de qui écrira un front (D-024).
 *
 * **CE QUE CE FICHIER NE PORTE PAS, ET POURQUOI.** Le dépôt d'un fichier
 * (`POST /api/v1/espace/dossiers/:id/fichiers`) est un envoi **multipart** : son
 * corps n'est pas un objet JSON, et il ne se décrit donc pas par un schéma
 * d'entrée. Sa forme vit sur la route, comme celle du dépôt d'image du tableau.
 */
/** Identifiant opaque d'une ligne. Jamais interprété côté client. */
const identifiant = z.string().trim().min(1).max(64);
/**
 * Nom d'un dossier.
 *
 * **Aucune borne n'est écrite au corpus.** Celle-ci empêche qu'un corps énorme
 * occupe le processus ; elle ne borne pas un intitulé que quelqu'un écrirait
 * vraiment. **Le nom est comparé tel quel** : le produit « ne renomme pas et
 * n'écrase pas » (Q439, Q452).
 */
const nomDeDossier = z.string().trim().min(1).max(255);
// ---------------------------------------------------------------------------
// F-33 — l'arborescence
// ---------------------------------------------------------------------------
/** `POST /api/v1/espace/dossiers` — F-33, et F-37 pour un dossier reçu. */
export const creationDossierSchema = z.strictObject({
    nom: nomDeDossier.describe('Nom du dossier. Aucun versionnement et aucun écrasement n’existent nulle ' +
        'part dans le produit : un nom est pris ou il ne l’est pas.'),
    parentId: identifiant
        .nullish()
        .describe('Le dossier parent. Absent, le dossier naît à la racine de votre espace. ' +
        'La profondeur des sous-dossiers est illimitée. Un destinataire de ' +
        'partage crée des sous-dossiers DANS le dossier reçu, jamais à côté : ' +
        'le parent doit alors être ce dossier ou l’un de ses descendants.'),
});
/**
 * `PATCH /api/v1/espace/dossiers/:id` — F-33, renommer **et** déplacer.
 *
 * **Les deux gestes passent par la même route, et le troisième a la sienne** :
 * F-33 énumère « renommer, déplacer, supprimer », et la suppression est un
 * `DELETE`. Réunir les deux premiers évite qu'un déplacement suivi d'un
 * renommage laisse un état intermédiaire visible.
 */
export const modificationDossierSchema = z
    .strictObject({
    nom: nomDeDossier
        .optional()
        .describe('Le nouveau nom. Absent, le nom ne change pas.'),
    parentId: identifiant
        .nullish()
        .describe('Le nouveau parent. `null` remonte le dossier à la racine de votre ' +
        'espace. Absent, l’emplacement ne change pas. Un dossier partagé qui ' +
        'se déplace GARDE son partage ; il ne peut pas entrer dans un dossier ' +
        'reçu d’autrui, ce qui lui ferait changer de propriétaire.'),
})
    // Un corps vide serait un geste sans effet qui répondrait 200 : le refuser
    // rend la faute visible au lieu de la laisser passer pour un succès.
    .refine((v) => v.nom !== undefined || v.parentId !== undefined, {
    message: 'Indiquez au moins un nom ou un parent.',
});
// ---------------------------------------------------------------------------
// F-37 — le partage d'un dossier
// ---------------------------------------------------------------------------
/** `POST /api/v1/espace/dossiers/:id/partages` — F-37. */
export const partageDossierSchema = z.strictObject({
    destinatairesIds: z
        .array(identifiant)
        .min(1)
        .max(50)
        .describe('Les personnes à qui ce dossier est partagé, choisies parmi vos ' +
        'relations. On partage à plusieurs en un geste, et le produit crée ' +
        'autant de partages DISTINCTS, retirables séparément. Une relation est ' +
        'le préalable de chacun.'),
});
//# sourceMappingURL=fichiers.contract.js.map